import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Ticket, KeyRound, ShieldCheck, AlertTriangle, MapPin, Users, ArrowUpDown, X, Lock, SlidersHorizontal, ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { B2bFiscaalFilter, B2bInkoopType, B2bLeverlocatie, B2bRijInfo, B2bSortering, WkrRuimte } from '../../services/b2bAcademyApi';
import { LEGE_B2B_FILTERS, MAX_GROEP, groepUit, heeftB2bFilters, type B2bFilters } from './b2bFilters';
import { eur } from './b2bStijl';

export const LEVERLOCATIE_OPTIES: Array<{ key: B2bLeverlocatie; emoji: string; label: string; uitleg: string }> = [
  { key: 'eigen_locatie', emoji: '🏢', label: 'On-Site (Bedrijfslocatie)', uitleg: 'De trainer reist naar uw kantoor of fabriek' },
  { key: 'extern', emoji: '🏫', label: 'Externe locatie', uitleg: 'De opleider verzorgt een representatieve trainingsruimte' },
  { key: 'online', emoji: '💻', label: 'Live-Online Classroom', uitleg: '100% digitaal en interactief via Teams of Zoom' },
];

export const INKOOP_OPTIES: Array<{ key: B2bInkoopType; icon: LucideIcon; label: string }> = [
  { key: 'incompany', icon: Building2, label: 'Incompany Maatwerk' },
  { key: 'bulk_seats', icon: Ticket, label: 'Bulk-Seats' },
  { key: 'licentie', icon: KeyRound, label: 'Licenties' },
];

export const SORT_OPTIES: Array<{ key: B2bSortering; label: string }> = [
  { key: 'relevantie', label: 'Relevantie' },
  { key: 'populair', label: '🔥 Meest Populair' },
  { key: 'roi', label: '⭐ Hoogste ROI' },
  { key: 'impact', label: '🎯 Hoogste Impact' },
  { key: 'prijs', label: '💶 Laagste Vanaf-Prijs' },
];

const kop = 'text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const knop = (actief: boolean, extra = '') =>
  `flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-xs font-semibold transition ${actief ? 'border-[#0B1F3A] bg-[#0B1F3A] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'} ${extra}`;

/** Aan/uit-schakelaar voor de fiscale toggles. */
const Schakelaar: React.FC<{ aan: boolean; kleur: string; uit?: boolean }> = ({ aan, kleur, uit }) => (
  <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${uit ? 'bg-gray-200' : aan ? '' : 'bg-gray-300'}`} style={aan && !uit ? { backgroundColor: kleur } : undefined} aria-hidden="true">
    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition ${aan ? 'translate-x-4' : 'translate-x-0.5'}`} />
  </span>
);

/** Labels van de actieve filters, gedeeld door de badges in de workspace en de mobiele teller. */
function useFilterTags(filters: B2bFilters, rijen: B2bRijInfo[], onChange: (next: B2bFilters) => void) {
  const { t } = useTranslation('pages');
  const zet = (patch: Partial<B2bFilters>) => onChange({ ...filters, ...patch });
  const tags: Array<{ key: string; label: string; wis: () => void }> = [];
  if (filters.q) tags.push({ key: 'q', label: `“${filters.q}”`, wis: () => zet({ q: null }) });
  if (filters.rij) {
    const r = rijen.find((x) => x.key === filters.rij);
    tags.push({ key: 'rij', label: r ? `${r.emoji} ${r.badge}` : filters.rij, wis: () => zet({ rij: null }) });
  }
  if (filters.vorm) {
    const o = INKOOP_OPTIES.find((x) => x.key === filters.vorm)!;
    tags.push({ key: 'vorm', label: t(`b2bAcademy.discovery.inkoop.${o.key}`, { defaultValue: o.label }), wis: () => zet({ vorm: null }) });
  }
  for (const f of filters.fiscaal) {
    tags.push({
      key: `fiscaal-${f}`,
      label: f === 'wkr' ? t('b2bAcademy.discovery.wkrBelast', { defaultValue: 'WKR-Belast' }) : t('b2bAcademy.discovery.vrijgesteld', { defaultValue: '100% Gericht Vrijgesteld' }),
      wis: () => zet({ fiscaal: filters.fiscaal.filter((x) => x !== f) }),
    });
  }
  for (const l of filters.locatie) {
    const o = LEVERLOCATIE_OPTIES.find((x) => x.key === l)!;
    tags.push({ key: `loc-${l}`, label: `${o.emoji} ${t(`b2bAcademy.discovery.locatie.${l}`, { defaultValue: o.label })}`, wis: () => zet({ locatie: filters.locatie.filter((x) => x !== l) }) });
  }
  if (filters.groep) tags.push({ key: 'groep', label: t('b2bAcademy.discovery.groepTag', { defaultValue: '{{n}} deelnemers', n: filters.groep }), wis: () => zet({ groep: null }) });
  if (filters.alles) tags.push({ key: 'alles', label: t('b2bAcademy.alleTrajecten', { defaultValue: 'Alle incompany-trajecten' }), wis: () => zet({ alles: false }) });
  return tags;
}

/** Actieve filterbadges + "Terug naar de catalogus" bovenaan de hoofdworkspace. */
export const B2bActieveFilters: React.FC<{ filters: B2bFilters; rijen: B2bRijInfo[]; onChange: (next: B2bFilters) => void }> = ({ filters, rijen, onChange }) => {
  const { t } = useTranslation('pages');
  const tags = useFilterTags(filters, rijen, onChange);
  if (!heeftB2bFilters(filters)) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label={t('b2bAcademy.discovery.actieveFilters', { defaultValue: 'Actieve filters' })}>
      {tags.map((tag) => (
        <button key={tag.key} type="button" onClick={tag.wis} className="inline-flex items-center gap-1 rounded-full border border-sky-400/40 bg-sky-400/10 px-2.5 py-1 text-xs font-medium text-sky-800 hover:bg-sky-400/20">
          <X className="h-3 w-3" aria-hidden="true" />
          {tag.label}
        </button>
      ))}
      <button type="button" onClick={() => onChange({ ...LEGE_B2B_FILTERS, sort: filters.sort })} className="ml-1 text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline">
        {t('b2bAcademy.discovery.terugNaarHome', { defaultValue: 'Terug naar de catalogus' })}
      </button>
    </div>
  );
};

/**
 * Sidebar filter- & sorteer-poort (Academy UI Overhaul): alle B2B-inputs verticaal in de
 * linkerkolom — zoeker (children), sortering op relevantie, omgeving-type, inkoopmodel,
 * fiscale toggles (met WKR-Grendel) en de vrij te kiezen groepsgrootte. Onder lg klapt het
 * paneel uit via een "Filters"-knop.
 */
export const B2bFilterbalk: React.FC<{
  filters: B2bFilters;
  rijen: B2bRijInfo[];
  wkr: WkrRuimte | undefined;
  onChange: (next: B2bFilters) => void;
  children?: React.ReactNode;
}> = ({ filters, rijen, wkr, onChange, children }) => {
  const { t } = useTranslation('pages');
  const zet = (patch: Partial<B2bFilters>) => onChange({ ...filters, ...patch });
  const wkrOp = Boolean(wkr?.op);
  const aantalActief = useFilterTags(filters, rijen, onChange).filter((tag) => tag.key !== 'q').length;
  const [mobielOpen, setMobielOpen] = useState(false);

  // Groepsgrootte: lokaal typen, pas bij blur/Enter in de URL.
  const [groepInvoer, setGroepInvoer] = useState(filters.groep ? String(filters.groep) : '');
  useEffect(() => setGroepInvoer(filters.groep ? String(filters.groep) : ''), [filters.groep]);
  const bevestigGroep = () => {
    const n = groepUit(groepInvoer);
    if (n !== filters.groep) zet({ groep: n });
    if (!n) setGroepInvoer('');
  };

  const toggleFiscaal = (f: B2bFiscaalFilter) => {
    if (f === 'wkr' && wkrOp) return;
    const next = filters.fiscaal.includes(f) ? filters.fiscaal.filter((x) => x !== f) : [...filters.fiscaal, f];
    // WKR-belast uitsluitend de rij Cultuur & Teambuilding: een actieve andere rij botst → rij loslaten
    const rij = f === 'wkr' && next.includes('wkr') && !next.includes('vrijgesteld') && filters.rij && filters.rij !== 'passie' ? null : filters.rij;
    zet({ fiscaal: next, rij });
  };
  const toggleLocatie = (l: B2bLeverlocatie) => zet({ locatie: filters.locatie.includes(l) ? filters.locatie.filter((x) => x !== l) : [...filters.locatie, l] });

  const vrij = filters.fiscaal.includes('vrijgesteld');
  const wkrAan = filters.fiscaal.includes('wkr');

  return (
    <div role="search" aria-label={t('b2bAcademy.discovery.filterbalk', { defaultValue: 'B2B filter-, sorteer- en zoekpaneel' })}>
      <button
        type="button"
        onClick={() => setMobielOpen((o) => !o)}
        aria-expanded={mobielOpen}
        className="flex w-full items-center justify-between gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-900 lg:hidden"
      >
        <span className="inline-flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-gray-500" aria-hidden="true" />
          {aantalActief > 0
            ? t('b2bAcademy.discovery.filtersKnopActief', { defaultValue: 'Filters ({{n}} actief)', n: aantalActief })
            : t('b2bAcademy.discovery.filtersKnop', { defaultValue: 'Zoeken & filters' })}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition ${mobielOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      <div className={`${mobielOpen ? 'mt-2 block' : 'hidden'} space-y-5 rounded-2xl border border-gray-200 bg-white p-4 lg:mt-0 lg:block`}>
        {/* 1. Zoekgrendels & Advanced Relevance Sorter */}
        <section className="space-y-2">
          <h2 className={kop}>{t('b2bAcademy.discovery.zoekKop', { defaultValue: 'Zoeken' })}</h2>
          {children}
        </section>

        <section className="space-y-2">
          <h2 className={kop}>{t('b2bAcademy.discovery.sorteerKop', { defaultValue: 'Sorteer op' })}</h2>
          <label className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900">
            <ArrowUpDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
            <span className="sr-only">{t('b2bAcademy.discovery.sorteer', { defaultValue: 'Sorteren' })}</span>
            <select value={filters.sort} onChange={(e) => zet({ sort: e.target.value as B2bSortering })} className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none">
              {SORT_OPTIES.map((o) => (
                <option key={o.key} value={o.key}>{t(`b2bAcademy.discovery.sort.${o.key}`, { defaultValue: o.label })}</option>
              ))}
            </select>
          </label>
        </section>

        {/* 2. Logistieke & fiscale filters */}
        <fieldset className="space-y-1.5">
          <legend className={`${kop} mb-2 flex items-center gap-1.5`}>
            <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
            {t('b2bAcademy.discovery.omgeving', { defaultValue: 'Omgeving-type' })}
          </legend>
          {LEVERLOCATIE_OPTIES.map((o) => {
            const actief = filters.locatie.includes(o.key);
            return (
              <label key={o.key} title={t(`b2bAcademy.discovery.locatieUitleg.${o.key}`, { defaultValue: o.uitleg })} className={`${knop(actief)} cursor-pointer select-none`}>
                <input type="checkbox" checked={actief} onChange={() => toggleLocatie(o.key)} className="sr-only" />
                <span aria-hidden="true">{o.emoji}</span>
                {t(`b2bAcademy.discovery.locatie.${o.key}`, { defaultValue: o.label })}
              </label>
            );
          })}
        </fieldset>

        <fieldset className="space-y-1.5">
          <legend className={`${kop} mb-2`}>{t('b2bAcademy.discovery.inkoopmodel', { defaultValue: 'Inkoopmodel' })}</legend>
          {INKOOP_OPTIES.map((o) => {
            const actief = filters.vorm === o.key;
            const Icon = o.icon;
            return (
              <button key={o.key} type="button" aria-pressed={actief} onClick={() => zet({ vorm: actief ? null : o.key })} className={knop(actief)}>
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t(`b2bAcademy.discovery.inkoop.${o.key}`, { defaultValue: o.label })}
              </button>
            );
          })}
        </fieldset>

        <fieldset className="space-y-1.5">
          <legend className={`${kop} mb-2`}>{t('b2bAcademy.discovery.fiscaalKop', { defaultValue: 'Fiscale status' })}</legend>
          <button type="button" role="switch" aria-checked={vrij} onClick={() => toggleFiscaal('vrijgesteld')} className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left text-xs font-semibold text-gray-700 hover:border-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
              {t('b2bAcademy.discovery.vrijgesteld', { defaultValue: '100% Gericht Vrijgesteld' })}
            </span>
            <Schakelaar aan={vrij} kleur="#059669" />
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={wkrAan}
            disabled={wkrOp}
            onClick={() => toggleFiscaal('wkr')}
            title={wkrOp ? t('b2bAcademy.discovery.wkrGrendelTitel', { defaultValue: 'De vrije ruimte van uw organisatie is op — WKR-belaste trajecten zijn tijdelijk verborgen.' }) : undefined}
            className="flex w-full items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left text-xs font-semibold text-gray-700 hover:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-200"
          >
            <span className="inline-flex items-center gap-1.5">
              {wkrOp ? <Lock className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" /> : <AlertTriangle className="h-3.5 w-3.5 text-orange-600" aria-hidden="true" />}
              {t('b2bAcademy.discovery.wkrBelast', { defaultValue: 'WKR-Belast' })}
            </span>
            <Schakelaar aan={wkrAan} kleur="#FF9800" uit={wkrOp} />
          </button>
          {wkr && wkr.resterend !== null && (
            <p className={`px-1 text-[11px] ${wkrOp ? 'text-orange-700' : 'text-gray-500'}`}>
              {wkrOp
                ? t('b2bAcademy.discovery.wkrOp', { defaultValue: 'Vrije ruimte op' })
                : t('b2bAcademy.discovery.wkrRest', { defaultValue: 'vrije ruimte {{bedrag}}', bedrag: eur.format(wkr.resterend) })}
            </p>
          )}
        </fieldset>

        <section className="space-y-2">
          <h2 className={kop}>
            <label htmlFor="b2b-groepsgrootte">{t('b2bAcademy.discovery.groepsgrootte', { defaultValue: 'Groepsgrootte' })}</label>
          </h2>
          <div className="flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 focus-within:border-sky-500">
            <Users className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
            <input
              id="b2b-groepsgrootte"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_GROEP}
              step={1}
              value={groepInvoer}
              onChange={(e) => setGroepInvoer(e.target.value)}
              onBlur={bevestigGroep}
              onKeyDown={(e) => e.key === 'Enter' && bevestigGroep()}
              placeholder={t('b2bAcademy.discovery.groepAlle', { defaultValue: 'Elke groepsgrootte' })}
              className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
            />
            {filters.groep && (
              <button type="button" onClick={() => zet({ groep: null })} aria-label={t('b2bAcademy.wissen', { defaultValue: 'Wissen' })} className="text-gray-400 hover:text-gray-700">
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
          <p className="px-1 text-[11px] text-gray-500">{t('b2bAcademy.discovery.groepUitleg', { defaultValue: 'Vrij te kiezen aantal deelnemers; toont trajecten waarvan de staffel deze groep omvat.' })}</p>
        </section>
      </div>
    </div>
  );
};

export default B2bFilterbalk;
